// src/data/career.ts
import type { Language } from "@/lib/i18n";
import { HiTrendingUp, HiUsers, HiHeart, HiBriefcase } from "react-icons/hi";
import type { IconType } from "react-icons";

interface CareerBenefit {
  id: number;
  icon: IconType;
  title: string;
  desc: string;
}

interface JobOpening {
  id: number;
  role: string;
  type: string;
  department: string;
  location: string;
  description: string;
  requirements: string[];
}

const careerBenefitsByLang: Record<Language, CareerBenefit[]> = {
  id: [
    { id: 1, icon: HiTrendingUp, title: "Pertumbuhan Karir", desc: "Program pelatihan intensif dan jenjang karir yang jelas untuk pengembangan profesional Anda." },
    { id: 2, icon: HiUsers, title: "Tim yang Mendukung", desc: "Lingkungan kerja yang kolaboratif, inklusif, dan saling mendukung seperti keluarga." },
    { id: 3, icon: HiHeart, title: "Kesehatan & Wellness", desc: "Asuransi kesehatan lengkap dan fasilitas gym gratis untuk menjaga kebugaran karyawan." },
    { id: 4, icon: HiBriefcase, title: "Gaji Kompetitif", desc: "Paket remunerasi menarik termasuk bonus kinerja dan tunjangan hari raya." },
  ],
  en: [
    { id: 1, icon: HiTrendingUp, title: "Career Growth", desc: "Intensive training programs and clear career paths for your professional development." },
    { id: 2, icon: HiUsers, title: "Supportive Team", desc: "Collaborative, inclusive work environment with family-like support." },
    { id: 3, icon: HiHeart, title: "Health & Wellness", desc: "Comprehensive health insurance and free gym access to maintain employee fitness." },
    { id: 4, icon: HiBriefcase, title: "Competitive Salary", desc: "Attractive remuneration packages including performance bonuses and holiday allowances." },
  ],
};

const jobOpeningsByLang: Record<Language, JobOpening[]> = {
  id: [
    {
      id: 1, role: "Front-End Developer", type: "Full-Time", department: "Teknologi", location: "Denpasar, Bali",
      description: "Kami mencari Front-End Developer yang passionate dengan UI/UX dan memiliki pengalaman kuat dalam React/Next.js untuk mengembangkan platform digital KinKen.",
      requirements: [
        "Minimal 2 tahun pengalaman menggunakan React.js & Next.js.",
        "Memahami Tailwind CSS dan Framer Motion.",
        "Memiliki portofolio website yang interaktif dan responsif.",
        "Mampu bekerja sama dengan tim UI/UX Designer.",
      ],
    },
    {
      id: 2, role: "Community Manager", type: "Full-Time", department: "Operasional", location: "Denpasar, Bali",
      description: "Bertanggung jawab untuk membangun hubungan baik antar penghuni, mengelola event komunitas, dan memastikan kenyamanan tinggal di KinKen Kost Elite.",
      requirements: [
        "Pengalaman di bidang hospitality atau customer service minimal 1 tahun.",
        "Kemampuan komunikasi yang sangat baik (Indonesia & Inggris).",
        "Berjiwa extrovert, ramah, dan solutif.",
        "Bersedia bekerja dengan sistem shift.",
      ],
    },
    {
      id: 3, role: "Housekeeping Supervisor", type: "Full-Time", department: "Operasional", location: "Denpasar, Bali",
      description: "Memimpin tim kebersihan untuk memastikan standar kebersihan kamar dan area umum KinKen selalu setara dengan hotel bintang lima.",
      requirements: [
        "Pengalaman sebagai Housekeeping di hotel minimal 3 tahun.",
        "Memahami standar kebersihan dan penggunaan bahan kimia pembersih.",
        "Memiliki jiwa kepemimpinan dan detail-oriented.",
        "Mampu menyusun jadwal kerja tim.",
      ],
    },
  ],
  en: [
    {
      id: 1, role: "Front-End Developer", type: "Full-Time", department: "Technology", location: "Denpasar, Bali",
      description: "We're looking for a passionate Front-End Developer with strong UI/UX skills and experience in React/Next.js to develop KinKen's digital platform.",
      requirements: [
        "Minimum 2 years experience with React.js & Next.js.",
        "Proficient in Tailwind CSS and Framer Motion.",
        "Portfolio of interactive and responsive websites.",
        "Able to collaborate with UI/UX Designers.",
      ],
    },
    {
      id: 2, role: "Community Manager", type: "Full-Time", department: "Operations", location: "Denpasar, Bali",
      description: "Responsible for building positive relationships among residents, managing community events, and ensuring a comfortable living experience at KinKen Kost Elite.",
      requirements: [
        "Minimum 1 year experience in hospitality or customer service.",
        "Excellent communication skills (Indonesian & English).",
        "Outgoing, friendly, and solution-oriented personality.",
        "Willing to work in shifts.",
      ],
    },
    {
      id: 3, role: "Housekeeping Supervisor", type: "Full-Time", department: "Operations", location: "Denpasar, Bali",
      description: "Lead the cleaning team to ensure KinKen's room and common area cleanliness standards are always on par with five-star hotels.",
      requirements: [
        "Minimum 3 years experience as Hotel Housekeeping.",
        "Understanding of cleanliness standards and cleaning chemicals.",
        "Leadership skills and detail-oriented.",
        "Able to create team work schedules.",
      ],
    },
  ],
};

// Backward compatibility
export const CAREER_BENEFITS = careerBenefitsByLang.id;
export const JOB_OPENINGS = jobOpeningsByLang.id;

// i18n functions
export function getCareerBenefits(lang: Language = "id"): CareerBenefit[] {
  return careerBenefitsByLang[lang] || careerBenefitsByLang.id;
}

export function getJobOpenings(lang: Language = "id"): JobOpening[] {
  return jobOpeningsByLang[lang] || jobOpeningsByLang.id;
}
